import React, { useState } from 'react';
import {
  BookOpen, Shield, Users, ScanLine, Brain, CheckCircle2,
  Database, FileImage, History, Award, ArrowRight, ChevronDown,
  GraduationCap, Building2, Sparkles, Lock, Search
} from 'lucide-react';

const FEATURES = [
  {
    icon: ScanLine,
    title: 'Scanner Integration',
    desc: 'Direct TWAIN/WiFi scanner support to digitize physical mark sheets without manual data entry.',
    color: 'bg-blue-50 text-blue-600',
  },
  {
    icon: Brain,
    title: 'AI-Powered Extraction',
    desc: 'Google Gemini and other AI models automatically extract roll numbers, marks and subject data from scanned images.',
    color: 'bg-purple-50 text-purple-600',
  },
  {
    icon: CheckCircle2,
    title: 'Dual-Column Verification',
    desc: 'Automatically detects mismatches between Tab 1 and Tab 2 columns, flagging discrepancies for human review.',
    color: 'bg-green-50 text-green-600',
  },
  {
    icon: Database,
    title: 'Secure Data Storage',
    desc: 'Verified marks are stored in a centralized MySQL database, accessible only to authorized NEB officials.',
    color: 'bg-amber-50 text-amber-600',
  },
  {
    icon: FileImage,
    title: 'File Management',
    desc: 'Upload, track, and reprocess mark sheet images with full audit trails and processing status.',
    color: 'bg-sky-50 text-sky-600',
  },
  {
    icon: History,
    title: 'Process History',
    desc: 'Complete history of all digitization jobs — who processed what, when, with how many records saved.',
    color: 'bg-rose-50 text-rose-600',
  },
  {
    icon: Award,
    title: 'Student Result Portal',
    desc: 'Students verify their results online using Symbol Number and Date of Birth — no login required.',
    color: 'bg-emerald-50 text-emerald-600',
  },
  {
    icon: Users,
    title: 'Role-Based Access',
    desc: 'Separate roles for Admin, Digitizer, Verifier and Viewer ensure each user sees only what they need.',
    color: 'bg-indigo-50 text-indigo-600',
  },
];

const EXAM_TYPES = [
  { name: 'SEE', full: 'Secondary Education Examination', grade: 'Grade 10', color: 'from-green-500 to-teal-600' },
  { name: 'Grade 12', full: 'National Examination Board', grade: 'Grade 11 / 12', color: 'from-blue-600 to-indigo-700' },
];

function AccessCard({ icon: Icon, title, subtitle, desc, btnLabel, btnColor, onClick }) {
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 flex flex-col items-center text-center hover:shadow-2xl transition-shadow group">
      <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mb-5 ${btnColor} bg-opacity-10`}>
        <Icon className={`w-10 h-10 ${btnColor.replace('bg-', 'text-')}`} />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-1">{title}</h3>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{subtitle}</p>
      <p className="text-sm text-gray-500 mb-7 leading-relaxed">{desc}</p>
      <button
        onClick={onClick}
        className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm text-white transition-all group-hover:gap-3 ${btnColor}`}
      >
        {btnLabel} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function LandingPage({ onLoginClick, onResultClick }) {
  const [faqOpen, setFaqOpen] = useState(null);

  const faqs = [
    { q: 'Who can use this system?', a: 'NEB officials (admins, digitizers, verifiers) use the internal dashboard. Students access only the public result portal — no account needed.' },
    { q: 'How do students check their result?', a: 'Click "Check My Result" below, enter your Symbol Number and Date of Birth as registered with NEB, and your grade sheet will be displayed instantly.' },
    { q: 'Which exams are supported?', a: 'Currently SEE (Secondary Education Examination – Grade 10) and NEB Grade 12 results. The exam type is auto-detected during digitization.' },
    { q: 'Is the result data official?', a: 'Results are sourced directly from digitized NEB mark sheets. For any discrepancy, contact your examination centre or NEB directly.' },
  ];

  return (
    <div className="min-h-screen bg-white text-gray-800 font-sans">

      {/* ── Navbar ── */}
      <nav className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-blue-700 rounded-lg flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-sm font-bold text-gray-900 leading-tight">NEB Marks Digitizer</div>
              <div className="text-xs text-gray-400 leading-tight hidden sm:block">National Examinations Board, Nepal</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onResultClick}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors">
              <GraduationCap className="w-4 h-4" /> Check Result
            </button>
            <button onClick={onLoginClick}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold rounded-lg transition-colors">
              <Lock className="w-3.5 h-3.5" /> Official Login
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900 text-white">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-xs font-semibold mb-6">
            <Sparkles className="w-3.5 h-3.5 text-yellow-300" />
            AI-Powered · SEE & Grade 12 · Official NEB Portal
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold leading-tight mb-5">
            Nepal's Digital Marks<br className="hidden sm:block" /> Management System
          </h1>
          <p className="text-blue-100 text-base sm:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
            From scanner to database — NEB's centralized platform for digitizing, verifying
            and publishing examination results with AI-assisted accuracy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={onResultClick}
              className="flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-7 py-3.5 rounded-xl text-base shadow-lg transition-all">
              <GraduationCap className="w-5 h-5" /> Check My Result
            </button>
            <button onClick={onLoginClick}
              className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold px-7 py-3.5 rounded-xl text-base transition-all">
              <Shield className="w-5 h-5" /> NEB Officials Portal
            </button>
          </div>
        </div>
        {/* Exam type badges */}
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pb-16 flex flex-wrap justify-center gap-4">
          {EXAM_TYPES.map(et => (
            <div key={et.name}
              className={`bg-gradient-to-r ${et.color} rounded-xl px-5 py-3 flex items-center gap-3 shadow-md`}>
              <Award className="w-5 h-5 text-white/80" />
              <div className="text-white">
                <div className="font-bold text-sm">{et.name}</div>
                <div className="text-xs opacity-80">{et.full} · {et.grade}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Access Points ── */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Who are you?</h2>
          <p className="text-gray-500 text-sm">Choose the portal that applies to you</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <AccessCard
            icon={GraduationCap}
            title="I'm a Student"
            subtitle="Result Verification"
            desc="Check your SEE or Grade 12 result using your Symbol Number and Date of Birth. No account or login required."
            btnLabel="Check My Result"
            btnColor="bg-emerald-600"
            onClick={onResultClick}
          />
          <AccessCard
            icon={Building2}
            title="NEB Official"
            subtitle="Internal Dashboard"
            desc="Digitizers, verifiers, and administrators log in to process mark sheets, manage records, and publish results."
            btnLabel="Go to Official Portal"
            btnColor="bg-blue-700"
            onClick={onLoginClick}
          />
        </div>
      </section>

      {/* ── Features ── */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Platform Features</h2>
            <p className="text-gray-500 text-sm max-w-xl mx-auto">
              A complete end-to-end solution for NEB's examination result digitization pipeline
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURES.map(({ icon: Icon, title, desc, color }) => (
              <div key={title} className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-shadow">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <h4 className="font-semibold text-gray-800 text-sm mb-1.5">{title}</h4>
                <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works (NEB flow) ── */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">How It Works</h2>
          <p className="text-gray-500 text-sm">From physical mark sheet to published result in four steps</p>
        </div>
        <div className="grid sm:grid-cols-4 gap-6">
          {[
            { step: '01', icon: ScanLine,      title: 'Scan',     desc: 'Digitizer scans physical mark sheets using a USB or WiFi scanner.' },
            { step: '02', icon: Brain,         title: 'AI Extract', desc: 'AI reads both columns, extracts roll numbers and marks, flags mismatches.' },
            { step: '03', icon: CheckCircle2,  title: 'Verify',   desc: 'Verifier reviews flagged rows and approves the final dataset.' },
            { step: '04', icon: Award,         title: 'Publish',  desc: 'Approved marks are saved to the database and accessible to students.' },
          ].map(({ step, icon: Icon, title, desc }) => (
            <div key={step} className="relative text-center">
              <div className="w-12 h-12 bg-blue-700 text-white rounded-full flex items-center justify-center text-xs font-bold mx-auto mb-3">
                {step}
              </div>
              <Icon className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <h4 className="font-semibold text-gray-800 text-sm mb-1">{title}</h4>
              <p className="text-xs text-gray-500">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Student CTA ── */}
      <section className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white py-14">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <Search className="w-10 h-10 mx-auto mb-4 opacity-80" />
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">Check Your Result Now</h2>
          <p className="text-emerald-100 mb-7 text-sm">
            Enter your Symbol Number and Date of Birth to view your official grade sheet.
            No registration needed.
          </p>
          <button onClick={onResultClick}
            className="inline-flex items-center gap-2 bg-white text-emerald-700 font-bold px-8 py-3.5 rounded-xl hover:bg-emerald-50 transition-colors shadow-lg">
            <GraduationCap className="w-5 h-5" /> View My Grade Sheet
          </button>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left font-medium text-sm text-gray-800 hover:bg-gray-50 transition-colors">
                {f.q}
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform shrink-0 ml-3 ${faqOpen === i ? 'rotate-180' : ''}`} />
              </button>
              {faqOpen === i && (
                <div className="px-5 pb-4 text-sm text-gray-500 leading-relaxed border-t border-gray-100 pt-3">
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-gray-900 text-gray-400 py-8 text-center text-xs">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="w-6 h-6 bg-blue-700 rounded flex items-center justify-center">
            <BookOpen className="w-3 h-3 text-white" />
          </div>
          <span className="font-semibold text-white text-sm">NEB Marks Digitizer</span>
        </div>
        <p>© {new Date().getFullYear()} National Examinations Board, Nepal. All rights reserved.</p>
        <p className="mt-1 text-gray-600">For result discrepancies contact your examination centre or NEB directly.</p>
      </footer>
    </div>
  );
}
